import { useEffect, useMemo, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faTrash,
  faShareAlt,
  faDownload,
  faPen,
} from '@fortawesome/free-solid-svg-icons'
import Spinner from '../Spinner/Spinner'
import styles from './CartDetails.module.css'
import { toast } from 'react-toastify'
import Backdrop from '../Backdrop/Backdrop'
import axios from 'axios'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

const cartStatusColors = {
  processing: '#f59e0b',
  fulfilled: '#3b82f6',
  cancelled: '#ef4444',
}

export const CartDetails = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [loading, setLoading] = useState(false)
  const [cart, setCart] = useState(null)
  const [imagesDialogOpen, setImagesDialogOpen] = useState(false)
  const [imagesDialogData, setImagesDialogData] = useState({
    productImages: [],
    mockupImages: [],
    defaultImage: '',
  })
  const [orderImagesDialogOpen, setOrderImagesDialogOpen] = useState(false)
  const [confirmCartDeleteOpen, setConfirmCartDeleteOpen] = useState(false)
  const [confirmProductDelete, setConfirmProductDelete] = useState({
    open: false,
    productId: null,
  })
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState('')
  const getCart = async () => {
    setLoading(true)
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/user/carts/${id}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    )
    const data = await response.json()
    if (data.cart) {
      setCart(data.cart)
    }
    setLoading(false)
  }

  const handleProductSave = async (productId, data) => {
    await updateCartProduct(productId, {
      quantity: data.units,
      customisation: data.customisation,
    })
    await getCart()
  }

  const handleProductDelete = async (productId) => {
    await deleteCartProduct(productId)
    await getCart()
  }
  useEffect(() => {
    getCart()
  }, [id])

  const updateCartProduct = async (productId, data) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/user/carts/${id}/products/${productId}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            quantity: data.quantity,
            customisation: data.customisation,
          }),
        }
      )
    } catch (error) {
      console.log(error)
      toast.error('Failed to update product')
    }
  }

  const deleteCartProduct = async (productId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/user/carts/${id}/products/${productId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      )
    } catch (error) {
      console.log(error)
      toast.error('Failed to delete product')
    }
  }

  const handleShareLink = () => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(window.location.href)
        .then(() => {
          toast.success('Link copied successfully')
        })
        .catch(() => {
          toast.error('Failed to copy link')
        })
    } else {
      console.log('Failed to copy link')
      toast.error('Failed to copy link')
    }
  }
  const handleDownloadPDF = async () => {
    if (!cart) return

    try {
      toast.info('Generating PDF...')

      const pdf = new jsPDF('p', 'mm', 'a4')
      const pageHeight = 297
      const pageWidth = 210
      const margin = 10
      let currentY = margin

      const tempContainer = document.createElement('div')
      tempContainer.style.position = 'absolute'
      tempContainer.style.left = '-9999px'
      tempContainer.style.top = '0'
      tempContainer.style.width = `${pageWidth}mm`
      document.body.appendChild(tempContainer)

      const addElementToPdf = async (element, isLastElement = false) => {
        const canvas = await html2canvas(element, {
          scale: 1.2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          width: element.offsetWidth,
          height: element.offsetHeight,
        })

        const imgData = canvas.toDataURL('image/jpeg')
        const imgWidth = pageWidth - 2 * margin
        const imgHeight = (canvas.height * imgWidth) / canvas.width

        if (currentY + imgHeight > pageHeight - margin && currentY > margin) {
          pdf.addPage()
          currentY = margin
        }

        pdf.addImage(imgData, 'JPEG', margin, currentY, imgWidth, imgHeight)
        currentY += imgHeight + 5

        return imgHeight
      }

      const headerDiv = document.createElement('div')
      headerDiv.style.padding = '10px'
      headerDiv.style.width = `${pageWidth - 20}mm`
      headerDiv.innerHTML = `
              <h1 style="font-size: 24px; margin-bottom: 10px;">
                ${cart?.name || 'Untitled Cart'}
              </h1>
              <p style="font-size: 12px; color: #999; margin-top: 5px;">
                Created On: ${
                  cart?.createdAt
                    ? new Date(cart.createdAt).toLocaleDateString()
                    : '-'
                }
              </p>
              <h2 style="font-size: 18px; margin-top: 20px; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 5px;">
                PRODUCTS
              </h2>
            `
      tempContainer.appendChild(headerDiv)
      await addElementToPdf(headerDiv)
      headerDiv.remove()

      const products = cart.products ?? []

      for (let i = 0; i < products.length; i++) {
        const product = products[i]
        const productDiv = document.createElement('div')
        productDiv.style.padding = '15px'
        productDiv.style.width = `${pageWidth - 20}mm`
        productDiv.style.marginBottom = '10px'
        productDiv.style.borderBottom =
          i < products.length - 1 ? '1px solid #ddd' : 'none'

        const imageSrc =
          product.image ||
          product.defaultImage ||
          product.images?.product?.[0] ||
          ''

        const itemDetails = Array.isArray(product.itemDetails)
          ? product.itemDetails
          : []

        const quantity = Number(product.quantity) || 0
        const rate = Number(product.rate ?? product.price ?? 0) || 0
        const gst = Number(product.gst) || 0
        const amount = Math.round(rate * (1 + gst / 100) * quantity)

        productDiv.innerHTML = `
                  <div style="display: flex; gap: 15px;">
                    ${
                      imageSrc
                        ? `
                      <img 
                        src="${imageSrc}" 
                        alt="${product.name || ''}"
                        style="width: 100px; height: 100px; object-fit: cover; flex-shrink: 0;"
                        crossorigin="anonymous"
                      />
                    `
                        : ''
                    }
                    <div style="flex: 1;">
                      <h3 style="font-size: 16px; margin-bottom: 5px;">
                        ${product.name || '-'}
                      </h3>
                      <p style="font-size: 12px; color: #666; margin-bottom: 8px;">
                        Brand: ${product.brand || '-'}
                      </p>
                      
                      ${
                        product.description && product.description !== '-'
                          ? `
                        <p style="font-size: 11px; margin-bottom: 8px;">
                          Description: ${product.description}
                        </p>
                      `
                          : ''
                      }
                      
                      ${
                        itemDetails.length > 0
                          ? `
                        <div style="font-size: 11px; margin-bottom: 8px;">
                          <strong>Item Details:</strong>
                          <ul style="margin-left: 20px; margin-top: 4px;">
                            ${itemDetails
                              .map((detail) => `<li>${detail}</li>`)
                              .join('')}
                          </ul>
                        </div>
                      `
                          : ''
                      }
      
                      ${
                        product.customisation || product.remarks
                          ? `
                        <div style="font-size: 10px; color: #666; margin-top: 8px;">
                          ${
                            product.customisation
                              ? `<p>Customisation: ${product.customisation}</p>`
                              : ''
                          }
                          ${
                            product.remarks
                              ? `<p>Remarks: ${product.remarks}</p>`
                              : ''
                          }
                        </div>
                      `
                          : ''
                      }
      
                      <div style="margin-top: 12px; padding: 12px; border: 1px solid #ddd; background-color: #f9f9f9; border-radius: 4px;">
                        <div style="display: flex; justify-content: space-between; font-size: 11px;">
                          <div>
                            <p>Quantity: ${quantity}</p>
                            <p>Rate: Rs ${rate}</p>
                            <p>GST: ${gst}%</p>
                          </div>
                          <div style="text-align: right;">
                            <p style="font-weight: bold; font-size: 13px;">
                              Amount: Rs ${amount}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                `

        tempContainer.appendChild(productDiv)
        await addElementToPdf(productDiv, false)
        productDiv.remove()
      }

      const summaryDiv = document.createElement('div')
      summaryDiv.style.padding = '20px'
      summaryDiv.style.width = `${pageWidth - 20}mm`
      summaryDiv.style.border = '2px solid #333'
      summaryDiv.style.backgroundColor = '#f5f5f5'
      summaryDiv.style.borderRadius = '4px'
      summaryDiv.style.marginTop = '20px'

      const discount = Number(cart.discount) || 0

      summaryDiv.innerHTML = `
              <h2 style="font-size: 18px; margin-bottom: 15px;">SUMMARY</h2>
              
              ${
                discount > 0
                  ? `
                <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                  <span>Subtotal:</span>
                  <span>Rs ${subtotal}</span>
                </div>
                <div style="display: flex; justify-content: space-between; margin-bottom: 8px; color: #10b981;">
                  <span>Discount (${discount}%):</span>
                  <span>-Rs ${Math.round(subtotal - totalTaxableAmount)}</span>
                </div>
              `
                  : ''
              }
              
              <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
                <span>Total Taxable Amount:</span>
                <span>Rs ${totalTaxableAmount}</span>
              </div>
              
              <div style="display: flex; justify-content: space-between; margin-bottom: 15px;">
                <span>Total Tax:</span>
                <span>Rs ${totalTax}</span>
              </div>
              
              <div style="border-top: 2px solid #333; padding-top: 15px; display: flex; justify-content: space-between; font-size: 20px; font-weight: bold;">
                <span>TOTAL AMOUNT:</span>
                <span>Rs ${totalAmount}</span>
              </div>
            `

      tempContainer.appendChild(summaryDiv)
      await addElementToPdf(summaryDiv, true)
      summaryDiv.remove()

      const notes = cart?.notes

      if (notes) {
        const notesDiv = document.createElement('div')
        notesDiv.style.padding = '10px 0'
        notesDiv.style.width = `${pageWidth - 20}mm`

        const notesHtmlParts = []

        if (notes) {
          notesHtmlParts.push(`
            <div style="font-size: 16px; margin-top: 6px;">
              <strong>Notes:</strong>
            </div>
            <div style="font-size: 14px; margin-top: 2px;">
              ${notes}
            </div>
          `)
        }

        notesDiv.innerHTML = notesHtmlParts.join('')

        tempContainer.appendChild(notesDiv)
        await addElementToPdf(notesDiv, true)
        notesDiv.remove()
      }

      document.body.removeChild(tempContainer)

      const cartTitle = cart?.name || 'Cart'
      pdf.save(`${cartTitle.replace(/[^a-z0-9]/gi, '_')}.pdf`)

      toast.success('PDF generated successfully')
    } catch (error) {
      console.error('Failed to generate PDF:', error)
      toast.error('Failed to generate PDF')
    }
  }
  const handleDeleteCart = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/user/carts/${id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      )
      navigate('/carts')
    } catch (error) {
      console.log(error)
      toast.error('Failed to delete cart')
    }
  }

  const handleOpenImagesDialog = (product) => {
    setImagesDialogData({
      productImages: product?.images?.product ?? [],
      mockupImages: product?.images?.mockups ?? [],
      defaultImage:
        product?.defaultImage ?? product?.images?.product?.[0] ?? '',
    })
    setImagesDialogOpen(true)
  }

  const { totalTax, totalTaxableAmount, totalAmount, subtotal } =
    useMemo(() => {
      if (!cart || !cart.products || loading) {
        return {
          totalTax: 0,
          totalTaxableAmount: 0,
          totalAmount: 0,
          subtotal: 0,
        }
      }

      const discount = Number(cart.discount) || 0

      const totals = cart.products.reduce(
        (acc, product) => {
          const productPrice = product.cost ?? product.price ?? 0
          const cost = Number(productPrice) || 0
          const quantity = Number(product.quantity) || 0
          const gst = Number(product.gst) || 0

          const discountedCost = cost * (1 - discount / 100)
          const amountWithoutTax = discountedCost * quantity
          const amountWithTax = discountedCost * (1 + gst / 100) * quantity

          acc.totalAmountWithTax += amountWithTax
          acc.totalAmountWithoutTax += amountWithoutTax
          acc.subtotal += cost * quantity
          return acc
        },
        {
          totalAmountWithTax: 0,
          totalAmountWithoutTax: 0,
          subtotal: 0,
        }
      )

      const roundedTotalAmountWithTax = Math.round(totals.totalAmountWithTax)
      const roundedTotalAmountWithoutTax = Math.round(
        totals.totalAmountWithoutTax
      )
      const roundedTotalTax =
        roundedTotalAmountWithTax - roundedTotalAmountWithoutTax
      const roundedSubtotal = Math.round(totals.subtotal)

      return {
        totalTax: roundedTotalTax,
        totalTaxableAmount: roundedTotalAmountWithoutTax,
        totalAmount: roundedTotalAmountWithTax,
        subtotal: roundedSubtotal,
      }
    }, [cart, loading])

  const addProductsToCart = async (products) => {
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/user/carts/${id}/products`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            products: products?.map((item) => ({
              productId: item.productId,
              quantity: item.quantity,
            })),
          }),
        }
      )
      getCart()
    } catch (error) {
      console.log(error)
      toast.error('Failed to add products to cart')
    }
  }

  if (loading) {
    return <Spinner />
  }

  if (!cart && !loading) {
    return <h1>Cart not found</h1>
  }

  const handleStartEditName = () => {
    setEditedName(cart?.name ?? '')
    setIsEditingName(true)
  }

  const handleCancelEditName = () => {
    setIsEditingName(false)
    setEditedName('')
  }

  const handleSaveName = async () => {
    const trimmed = (editedName || '').trim()
    if (!trimmed) {
      toast.error('Name cannot be empty')
      return
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/user/carts/${id}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: trimmed }),
        }
      )

      if (!response.ok) {
        throw new Error('Failed to update cart name')
      }

      toast.success('Cart name updated')
      setIsEditingName(false)
      setEditedName('')
      await getCart()
    } catch (error) {
      console.error(error)
      toast.error('Failed to update cart name')
    }
  }
  return (
    <div className={styles.container}>
      <div className={styles.cartDetailsContainer}>
        <div className={styles.cartTitleRow}>
          {isEditingName ? (
            <>
              <input
                type='text'
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className={styles.cartNameInput}
              />
              <button
                type='button'
                className={styles.cartNameSaveButton}
                onClick={handleSaveName}
              >
                Save
              </button>
              <button
                type='button'
                className={styles.cartNameCancelButton}
                onClick={handleCancelEditName}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <h1>{cart?.name || 'Untitled'}</h1>
              {cart?.isOwner && (
                <button
                  type='button'
                  className={styles.cartNameEditButton}
                  onClick={handleStartEditName}
                >
                  <FontAwesomeIcon
                    icon={faPen}
                    className={styles.cartNameEditIcon}
                  />
                </button>
              )}
            </>
          )}
        </div>
        <div className={styles.buttonContainer}>
          <div className={styles.buttonGroup}>
            <button
              type='button'
              className={styles.cartActionButton}
              onClick={handleShareLink}
            >
              <FontAwesomeIcon
                icon={faShareAlt}
                className={styles.cartActionIcon}
              />
              Share Link
            </button>
            <button
              type='button'
              className={styles.cartActionButton}
              onClick={handleDownloadPDF}
            >
              <FontAwesomeIcon
                icon={faDownload}
                className={styles.cartActionIcon}
              />
              Download PDF
            </button>
            {cart?.isOwner && cart?.status === 'active-cart' && (
              <button
                type='button'
                className={`${styles.cartActionButton} ${styles.deleteButton}`}
                onClick={() => setConfirmCartDeleteOpen(true)}
              >
                <FontAwesomeIcon
                  icon={faTrash}
                  className={styles.cartActionIcon}
                />
                Delete Cart
              </button>
            )}
          </div>
          {cart?.status !== 'active-cart' && (
            <div className={styles.cartStatusContainer}>
              <div
                className={styles.cartStatusBadge}
                style={{ backgroundColor: cartStatusColors[cart?.status] }}
              >
                {cart?.status === 'cancelled' ? 'Draft' : cart?.status}
              </div>
            </div>
          )}
        </div>
        <div className={styles.cartItemsContainer}>
          <div className={styles.cartProductsColumn}>
            {(cart?.products ?? []).map((product) => (
              <ProductCard
                key={product._id}
                image={product.defaultImage}
                quantity={product.quantity}
                price={product.rate}
                gst={product.gst}
                customisation={product.customization}
                remarks={product.remarks}
                images={product.images}
                defaultImage={product.defaultImage}
                name={product.name}
                brand={product.brand}
                description={product.description}
                itemDetails={product.itemDetails}
                isOwner={
                  (cart?.isOwner && cart?.status === 'active-cart') ?? false
                }
                handleDelete={() =>
                  setConfirmProductDelete({
                    open: true,
                    productId: product._id,
                  })
                }
                handleSave={(data) => handleProductSave(product._id, data)}
                handleViewImages={() => handleOpenImagesDialog(product)}
              />
            ))}
            {cart?.isOwner && cart?.status === 'active-cart' && (
              <button
                onClick={() => navigate(`/catalogue/${id}/add`)}
                className={styles.addProductsButton}
              >
                Add Products
              </button>
            )}
          </div>
          <div className={styles.cartSummaryColumn}>
            <SummaryCard
              subtotal={subtotal}
              totalTaxableAmount={totalTaxableAmount}
              totalTax={totalTax}
              totalAmount={totalAmount}
              discount={cart?.discount ?? 0}
            />
            {cart?.notes && (
              <div
                className={styles.summaryContainer}
                style={{
                  flexDirection: 'column',
                }}
              >
                <p className={styles.summaryItem}>Notes</p>
                <div
                  className={styles.summaryNotes}
                  style={{ fontSize: 14, color: '#444' }}
                  dangerouslySetInnerHTML={{ __html: cart.notes }}
                />
              </div>
            )}
            {cart?.orderImages?.length > 0 && (
              <button
                type='button'
                className={styles.viewBttnSecondary}
                onClick={() => setOrderImagesDialogOpen(true)}
              >
                View Order Images
              </button>
            )}
          </div>
        </div>
        <ProductImagesDialog
          open={imagesDialogOpen}
          onClose={() => setImagesDialogOpen(false)}
          productImages={imagesDialogData.productImages}
          mockupImages={imagesDialogData.mockupImages}
          defaultImage={imagesDialogData.defaultImage}
        />
        <OrderImagesDialog
          open={orderImagesDialogOpen}
          onClose={() => setOrderImagesDialogOpen(false)}
          images={cart?.orderImages ?? []}
        />
      </div>
      <ConfirmDialog
        open={confirmCartDeleteOpen}
        title='Delete Cart'
        message='Are you sure you want to delete this cart?'
        confirmLabel='Delete'
        onCancel={() => setConfirmCartDeleteOpen(false)}
        onConfirm={async () => {
          setConfirmCartDeleteOpen(false)
          await handleDeleteCart()
        }}
      />
      <ConfirmDialog
        open={confirmProductDelete.open}
        title='Delete Product'
        message='Are you sure you want to delete this product from the cart?'
        confirmLabel='Delete'
        onCancel={() =>
          setConfirmProductDelete({ open: false, productId: null })
        }
        onConfirm={async () => {
          if (confirmProductDelete.productId) {
            await handleProductDelete(confirmProductDelete.productId)
          }
          setConfirmProductDelete({ open: false, productId: null })
        }}
      />
    </div>
  )
}

const ProductCard = ({
  name = '',
  brand = '',
  description = '',
  itemDetails = [''],
  image = '',
  price = 0,
  quantity = 1,
  gst = 0,
  customisation = '',
  remarks = '',
  handleViewImages = () => {},
  handleDelete = () => {},
  handleSave = () => {},
  images = null,
  defaultImage,
  isOwner = false,
}) => {
  const customisationOptions = [
    'Single Colour Screen Print',
    'Multi-colour ScreenPrint',
    'DTF',
    'Engraving',
    'Embroidery',
    'UV Print',
    'Digital Print',
    'FurnacePrint',
    'Embossing',
    'UV Embossed & Shape Cut',
    'N/A',
  ]
  const [customisationValue, setCustomisationValue] = useState(customisation)
  const [remarksValue, setRemarksValue] = useState(remarks)
  const [units, setUnits] = useState(quantity)
  const [rate, setRate] = useState(price)
  const [gstValue, setGstValue] = useState(gst)

  useEffect(() => {
    setCustomisationValue(customisation)
    setRemarksValue(remarks)
    setUnits(quantity)
    setRate(price)
    setGstValue(gst)
  }, [customisation, remarks, quantity, gst, price])

  return (
    <div className={styles.productCard}>
      <div className={styles.productImageWrapper}>
        <img src={image} alt={name} className={styles.productImage} />
        <button
          type='button'
          className={`${styles.viewBttnSecondary} ${styles.viewBttnMobile}`}
          onClick={handleViewImages}
        >
          View Images
        </button>
      </div>
      <div className={styles.productMainColumn}>
        <h2 className={styles.productTitle}>{name}</h2>
        <p className={styles.brand}>{brand}</p>
        <p className={styles.subtextHeading}>Description</p>
        <p className={styles.subtext}>{description}</p>
        <p className={styles.subtextHeading}>Item Details :</p>
        <div className={styles.subtextList}>
          {itemDetails.map((item, index) => (
            <div key={index} className={styles.subtext}>
              {index + 1}. {item}
            </div>
          ))}
        </div>
      </div>
      <div className={styles.productMiddleColumn}>
        <select
          className={styles.customisationSelect}
          value={customisationValue}
          onChange={(e) => setCustomisationValue(e.target.value)}
          disabled
        >
          <option value=''>Customization</option>
          {customisationOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <textarea
          className={styles.remarksTextarea}
          value={remarksValue}
          onChange={(e) => setRemarksValue(e.target.value)}
          placeholder='Remarks'
          disabled
        />
      </div>
      <div className={styles.productRightColumn}>
        <div className={styles.productTopButtons}>
          {isOwner && (
            <button
              type='button'
              className={styles.viewBttn}
              onClick={handleDelete}
            >
              Delete
            </button>
          )}
          <button
            type='button'
            className={`${styles.viewBttnSecondary} ${styles.viewBttnDesktop}`}
            onClick={handleViewImages}
          >
            View Images
          </button>
        </div>
        <div className={styles.pricingSection}>
          <div className={styles.pricingField}>
            <label className={styles.pricingLabel}>Units</label>
            <input
              type='number'
              className={styles.pricingInput}
              value={units}
              onChange={(e) => setUnits(Number(e.target.value))}
              min='1'
              disabled={!isOwner}
            />
          </div>
          <div className={styles.pricingField}>
            <label className={styles.pricingLabel}>Rate</label>
            <input
              type='number'
              className={styles.pricingInput}
              value={rate}
              onChange={(e) => setRate(Number(e.target.value))}
              disabled
            />
          </div>
          <div className={`${styles.pricingField} ${styles.gstField}`}>
            <label className={styles.pricingLabel}>GST</label>
            <select
              className={styles.pricingInput}
              value={gstValue}
              onChange={(e) => setGstValue(Number(e.target.value))}
              disabled
            >
              <option value={0}>0%</option>
              <option value={5}>5%</option>
              <option value={12}>12%</option>
              <option value={18}>18%</option>
              <option value={28}>28%</option>
            </select>
          </div>
          {isOwner && (
            <div className={styles.saveButtonRow}>
              <button
                type='button'
                className={styles.saveBttn}
                onClick={() =>
                  handleSave({
                    customisation: customisationValue,
                    remarks: remarksValue,
                    units,
                    rate,
                    gst: gstValue,
                  })
                }
              >
                Save
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const SummaryCard = ({
  subtotal = 0,
  totalTaxableAmount = 0,
  totalTax = 0,
  totalAmount = 0,
  discount = 0,
}) => {
  const formatCurrency = (value) =>
    `₹${Math.round(value).toLocaleString('en-IN')}/-`

  const hasDiscount = discount > 0
  const discountAmount = hasDiscount
    ? Math.max(0, subtotal - totalTaxableAmount)
    : 0

  return (
    <div className={styles.summarySection}>
      <div className={styles.summaryHeader}>Summary</div>

      {hasDiscount && (
        <>
          <div className={styles.summaryContainer}>
            <div className={styles.summaryItem}>Subtotal</div>
            <div className={styles.summaryValue}>
              {formatCurrency(subtotal)}
            </div>
          </div>
          <div className={styles.summaryContainer}>
            <div className={styles.summaryDiscountItem}>
              Discount ({discount}%)
            </div>
            <div className={styles.summaryDiscountValue}>
              -{formatCurrency(discountAmount)}
            </div>
          </div>
        </>
      )}

      <div className={styles.summaryContainer}>
        <div className={styles.summaryItem}>Total Taxable Amount</div>
        <div className={styles.summaryValue}>
          {formatCurrency(totalTaxableAmount)}
        </div>
      </div>
      <div className={styles.summaryContainer}>
        <div className={styles.summaryItem}>Total Tax</div>
        <div className={styles.summaryValue}>{formatCurrency(totalTax)}</div>
      </div>
      <div className={styles.divider} />
      <div className={styles.totalAmountContainer}>
        <div className={styles.summaryItem}>Total Amount</div>
        <div className={styles.summaryValue}>{formatCurrency(totalAmount)}</div>
      </div>
    </div>
  )
}

const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
}) => {
  if (!open) return null

  return (
    <>
      <Backdrop show={open} switch={onCancel} />
      <div className={styles.confirmDialog}>
        <div className={styles.confirmDialogContent}>
          {title && <h3 className={styles.confirmDialogTitle}>{title}</h3>}
          {message && <p className={styles.confirmDialogMessage}>{message}</p>}
          <div className={styles.confirmDialogActions}>
            <button
              type='button'
              className={styles.confirmDialogCancel}
              onClick={onCancel}
            >
              {cancelLabel}
            </button>
            <button
              type='button'
              className={styles.confirmDialogConfirm}
              onClick={onConfirm}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

const ProductImagesDialog = ({
  open,
  onClose,
  productImages = [],
  mockupImages = [],
  defaultImage = '',
}) => {
  const [selectedTab, setSelectedTab] = useState('product')

  useEffect(() => {
    // reset tab when dialog opens
    if (open) {
      setSelectedTab('product')
    }
  }, [open])

  if (!open) return null

  const currentImages = selectedTab === 'product' ? productImages : mockupImages
  const hasImages = currentImages && currentImages.length > 0

  return (
    <>
      <Backdrop show={open} switch={onClose} />
      <div className={styles.imagesDialog}>
        <div className={styles.imagesDialogHeader}>
          <button
            type='button'
            className={styles.imagesDialogClose}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className={styles.imagesTabs}>
          <button
            type='button'
            className={`${styles.imagesTab} ${
              selectedTab === 'product' ? styles.imagesTabActive : ''
            }`}
            onClick={() => setSelectedTab('product')}
          >
            Product
          </button>
          <button
            type='button'
            className={`${styles.imagesTab} ${
              selectedTab === 'mockup' ? styles.imagesTabActive : ''
            }`}
            onClick={() => setSelectedTab('mockup')}
          >
            Mockups
          </button>
        </div>
        <div className={styles.imagesContent}>
          {hasImages ? (
            <div className={styles.imagesGrid}>
              {currentImages.map((src, index) => (
                <div key={src || index} className={styles.imagesGridItem}>
                  <img src={src} alt={`image-${index + 1}`} />
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.noImagesText}>No images available</div>
          )}
        </div>
      </div>
    </>
  )
}

const OrderImagesDialog = ({ open, onClose, images = [] }) => {
  if (!open) return null

  const hasImages = images && images.length > 0

  return (
    <>
      <Backdrop show={open} switch={onClose} />
      <div className={styles.imagesDialog}>
        <div className={styles.imagesDialogHeader}>
          <button
            type='button'
            className={styles.imagesDialogClose}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className={styles.imagesTabs}>
          <button
            type='button'
            className={`${styles.imagesTab} ${styles.imagesTabActive}`}
          >
            Order Images
          </button>
        </div>
        <div className={styles.imagesContent}>
          {hasImages ? (
            <div className={styles.imagesGrid}>
              {images.map((src, index) => (
                <div key={src || index} className={styles.imagesGridItem}>
                  <img src={src} alt={`order-image-${index + 1}`} />
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.noImagesText}>No order images available</div>
          )}
        </div>
      </div>
    </>
  )
}
